import { useMutation, useQuery } from '@tanstack/react-query';

import { selectActiveAccount, useAuthStore } from '@features/auth';
import { queryClient } from '@lib/queryClient';
import type { ApiError } from '@/types/api.types';

import { getBusinessProfile, patchBusinessProfile } from '../api/businessProfileApi';
import type { BusinessProfile, BusinessProfilePatch } from '../types';

const profileKey = (accountId: string) => ['settings', 'profile', accountId] as const;

export function useBusinessProfile() {
  const activeAccount = useAuthStore(selectActiveAccount);
  const accountId = activeAccount?.accountId ?? '';

  const query = useQuery({
    queryKey: profileKey(accountId),
    queryFn: () => getBusinessProfile(accountId),
    enabled: !!accountId,
  });

  const mutation = useMutation<BusinessProfile, ApiError, BusinessProfilePatch>({
    mutationFn: (patch) => patchBusinessProfile(accountId, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(profileKey(accountId), updated);
    },
  });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    fetchError: query.error as ApiError | null,
    update: mutation.mutate,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
}
